// Page transition: conveyor-belt (current slides out as new slides in) or fade for entry/available pages.
// We intercept links, fetch HTML, run strip or fade animation, then init the new page.

// Selectors/IDs match base.twig (data-barba/barba-wrapper are legacy names; could be renamed in template + here).
const CONTAINER_SELECTOR = '[data-barba="container"]';
const STRIP_ID = 'page-strip';
const WRAPPER_ID = 'barba-wrapper';
const TRANSITION_DURATION_MS = 800;
const EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

function getWrapper() {
  return document.getElementById(WRAPPER_ID);
}
function getStrip() {
  return document.getElementById(STRIP_ID);
}
function getCurrentContainer() {
  const strip = getStrip();
  return strip ? strip.querySelector(CONTAINER_SELECTOR) : null;
}

function isMenuOpen() {
  const menuOverlay = document.getElementById('menu-overlay');
  return menuOverlay && (
    window.menuOverlayIsOpen === true ||
    document.body.classList.contains('menu-open') ||
    (menuOverlay.style.display === 'flex') ||
    (window.getComputedStyle(menuOverlay).display === 'flex')
  );
}

/** Close menu and artists overlays; returns a Promise that resolves when safe to start transition. */
function closeOverlaysBeforeTransition() {
  if (window.closeArtistsOverlay && window.isArtistsOverlayOpen && window.isArtistsOverlayOpen()) {
    window.closeArtistsOverlay();
  }
  if (isMenuOpen() && window.closeNavOverlay) {
    window.closeNavOverlay();
    return new Promise((resolve) => setTimeout(resolve, 850));
  }
  return Promise.resolve();
}

/** Fetch full HTML and return the innerHTML of the page container. */
function fetchPageContent(href) {
  return fetch(href, { headers: { Accept: 'text/html' } })
    .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
    .then((html) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const container = doc.querySelector(CONTAINER_SELECTOR);
      return container ? container.innerHTML : '';
    });
}

/** True if pathname is a work entry page (e.g. /works/my-work-slug). */
function isWorkEntryUrl(pathname) {
  const match = pathname && pathname.match(/^\/works\/([^/]+)$/);
  return !!(match && match[1] && match[1] !== 'available');
}

/** True if pathname is an exhibition entry page (e.g. /exhibitions/my-exhibition-slug). */
function isExhibitionEntryUrl(pathname) {
  const match = pathname && pathname.match(/^\/exhibitions\/([^/]+)$/);
  return !!(match && match[1]);
}

/** True if pathname is the available works page (e.g. /works/available or /works/available/artist-slug). */
function isAvailableWorksUrl(pathname) {
  return !!(pathname && pathname.match(/^\/works\/available(\/[^/]+)?$/));
}

/** True if pathname is the home/index page. */
function isIndexPath(pathname) {
  const p = pathname || '';
  return p === '/' || p === '';
}

/** Set body class (e.g. index-page) so header visibility matches the current page after a transition. */
function updateBodyClassForUrl(targetUrl) {
  try {
    const path = new URL(targetUrl, window.location.origin).pathname;
    if (path === '/' || path === '') {
      document.body.classList.add('index-page');
    } else {
      document.body.classList.remove('index-page');
    }
  } catch (_) {}
}

/** Run inits and hash scroll on the active container (after load or after transition). */
function initCurrentPage(container, pendingHash) {
  if (!container) return;
  initHomePage(container);
  initExhibitionEntryPage(container);
  initStockroomPage(container);
  initProductPage(container);
  initArtistPage(container);
  initExhibitionsPage(container);
  initCatalogueRequests(container);
  const aboutPage = container.querySelector('.about-page');
  if (aboutPage) aboutPage.classList.add('about-page-loaded');
  if (window.initFooterReveal) window.initFooterReveal(container);
  const hash = pendingHash || (typeof window.location !== 'undefined' ? window.location.hash : '');
  if (hash) {
    const selector = hash.startsWith('#') ? hash : '#' + hash;
    const target = container.querySelector(selector) || document.querySelector(selector);
    if (target) {
      const headerHeight = 80;
      const pos = target.getBoundingClientRect().top + (window.pageYOffset || 0) - headerHeight;
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  }
}

/**
 * Home hero: the artist names run as a wheel and the background follows
 * whichever name is on the centre line.
 *
 * The names are the same list repeated, so advancing is a plain translate and,
 * once a whole list has gone past, the track steps back by one list with the
 * transition off. Every repeat is identical, so that jump cannot be seen and
 * the wheel never runs out of names.
 */
const HOME_CYCLE_MS = 3000;

/*
 * The Contact panel's heading has to sit exactly on top of the word "Contact"
 * in the nav, so the panel is sized from where that word actually is rather
 * than from a fixed width — the nav's position is content-driven (it hangs off
 * the right edge by the width of the row) and does not track a percentage.
 *
 * Only possible while the nav is laid out: on the home page, or with the menu
 * overlay open. Anywhere else the CSS width stands, since there is no visible
 * "Contact" to line up with in the first place.
 */
const CONTACT_HEADING_INSET = 20;

function syncContactPanelWidth() {
  const nav = Array.from(document.querySelectorAll('.nav-contact')).find(function (el) {
    return el.getBoundingClientRect().width > 0;
  });
  if (!nav) return;

  const rect = nav.getBoundingClientRect();
  const textLeft = rect.left + (parseFloat(getComputedStyle(nav).paddingLeft) || 0);

  /*
   * The right edge comes from the nearest fixed ancestor — the home page, an
   * overlay, or the nav bar — because that is the box the panel is positioned
   * against. It is never read off the viewport, which matches neither
   * innerWidth nor, with a scrollbar present, clientWidth.
   */
  let frame = nav.parentElement;
  while (frame && getComputedStyle(frame).position !== 'fixed') {
    frame = frame.parentElement;
  }
  if (!frame) return;

  const width = Math.round(frame.getBoundingClientRect().right - textLeft + CONTACT_HEADING_INSET);

  if (width > 0) {
    document.documentElement.style.setProperty('--g-contact-width', width + 'px');
  }
}

window.addEventListener('resize', syncContactPanelWidth);

/* Module scope on purpose: both the home page and the artists overlay branch on
   this, and the overlay's code sits inside a nested closure that can see out. */
function isMobileViewport() {
  return !!(window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
}

function initHomePage(container) {
  const root = container.querySelector('[data-home]');
  if (!root || root.dataset.homeReady === 'true') return;
  root.dataset.homeReady = 'true';

  // Two different cycles, not one cycle laid out two ways: desktop steps
  // through the artists, mobile steps through the nav sections.
  if (isMobileViewport()) {
    initHomeSectionCycle(root);
    return;
  }

  const track = root.querySelector('[data-home-track]');
  const names = Array.from(root.querySelectorAll('.home-carousel-name'));
  // Only the artist backgrounds: the section ones belong to the mobile cycle,
  // and counting them here puts the names out of step with the images.
  const images = Array.from(root.querySelectorAll('.home-media-image--artist'));
  const count = images.length;
  if (!track || !names.length || !count) return;

  const slot = parseFloat(getComputedStyle(root).getPropertyValue('--g-home-slot')) || 36;
  // Start a whole list in, so there are already names above the centre line.
  let index = count * 2;

  function render(animate) {
    /*
     * The unanimated step back is not just a transform jump. It moves is-active
     * onto a different element — an identical-looking one, since the list
     * repeats — and those carry a colour transition, so without this the names
     * cross-fade into themselves and the ticker appears to flicker.
     */
    if (!animate) {
      track.style.transition = 'none';
      names.forEach(function (name) { name.style.transition = 'none'; });
    }

    track.style.transform = 'translateY(' + (-index * slot) + 'px)';

    names.forEach(function (name, i) {
      const d = Math.abs(i - index);
      name.classList.toggle('is-active', d === 0);
      name.classList.toggle('is-near', d === 1);
      name.classList.toggle('is-far', d === 2);
    });

    images.forEach(function (image, i) {
      image.classList.toggle('is-active', i === index % count);
    });

    if (!animate) {
      // Flush the jump, then hand the transitions back so the next step
      // animates normally.
      track.getBoundingClientRect();
      track.style.transition = '';
      names.forEach(function (name) { name.style.transition = ''; });
    }
  }

  render(false);
  // Force the layout so the first animated step transitions from this position
  // rather than from the untransformed one.
  track.getBoundingClientRect();

  setInterval(function () {
    index += 1;
    render(true);

    if (index >= count * 3) {
      setTimeout(function () {
        index -= count;
        render(false);
      }, HOME_CYCLE_MS / 2);
    }
  }, HOME_CYCLE_MS);
}

/**
 * Mobile home: each nav section takes a turn in white over its own background.
 * The row's own active item is Menu, which is hidden at this width, so the
 * visible items are exactly the four the design cycles.
 */
function initHomeSectionCycle(root) {
  const items = Array.from(root.querySelectorAll('.nav-row .nav-row-item'))
    .filter(function (item) { return getComputedStyle(item).display !== 'none'; });
  const images = Array.from(root.querySelectorAll('.home-media-image--section'));
  if (!items.length || !images.length) return;

  let index = 0;

  function render() {
    items.forEach(function (item, i) { item.classList.toggle('is-active', i === index); });
    images.forEach(function (image, i) {
      image.classList.toggle('is-active', i === index % images.length);
    });
  }

  render();
  setInterval(function () {
    index = (index + 1) % items.length;
    render();
  }, HOME_CYCLE_MS);
}

/**
 * Stockroom: filter collections by artist, and hand catalogue requests to the
 * existing global modal.
 *
 * Called from initCurrentPage, so it runs on hard load and again after every
 * soft navigation. initCurrentPage fires more than once on initial load (see
 * the setTimeout in initInitialPage), so this guards against binding twice.
 */
function initStockroomPage(container) {
  const root = container.querySelector('[data-stockroom]');
  if (!root || root.dataset.stockroomReady === 'true') return;
  root.dataset.stockroomReady = 'true';

  const filters = Array.from(root.querySelectorAll('[data-artist-filter]'));
  const collections = Array.from(root.querySelectorAll('[data-collection-artist]'));

  function apply(slug) {
    filters.forEach(function (filter) {
      filter.classList.toggle('is-active', (filter.getAttribute('data-artist-filter') || '') === slug);
    });

    collections.forEach(function (collection) {
      collection.hidden = slug !== '' && collection.getAttribute('data-collection-artist') !== slug;
    });
  }

  filters.forEach(function (filter) {
    filter.addEventListener('click', function () {
      apply(filter.getAttribute('data-artist-filter') || '');
    });
  });

  /*
   * ?artist= arrives from an artist page's Available Works button, so the
   * Stockroom opens on that artist rather than on everything. Ignored unless
   * the artist actually has a filter, which they will not if they have no
   * collections — leaving All selected rather than an empty page.
   */
  let requested = '';
  try {
    requested = new URL(window.location.href).searchParams.get('artist') || '';
  } catch (_) {}

  const known = filters.some(function (filter) {
    return filter.getAttribute('data-artist-filter') === requested;
  });

  if (requested && known) apply(requested);
}

/** Catalogue request buttons, wherever they appear. */
function initCatalogueRequests(container) {
  container.querySelectorAll('[data-catalogue-request]').forEach(function (button) {
    if (button.dataset.catalogueReady === 'true') return;
    button.dataset.catalogueReady = 'true';

    button.addEventListener('click', function () {
      window.dispatchEvent(new Event('catalogue-modal-open'));
    });
  });
}

/**
 * Bind paging dots to a scroll-snap track. Scrolling is native, so touch and
 * trackpad work with no JS; the dots reflect the position and can drive it.
 * Handles both axes, since these tracks switch orientation at the breakpoints.
 */
function bindSliderDots(track, dots) {
  if (!track || !dots.length) return;

  const slides = Array.from(track.children);

  function isVertical() {
    return track.scrollHeight > track.clientHeight;
  }

  // Measure from the slides themselves — the exhibition track mixes portrait
  // and landscape widths, so a fixed step would drift.
  function offsetOf(slide) {
    return isVertical() ? slide.offsetTop : slide.offsetLeft;
  }

  function update() {
    const scrolled = isVertical() ? track.scrollTop : track.scrollLeft;
    let index = 0;
    let best = Infinity;

    slides.forEach(function (slide, i) {
      const distance = Math.abs(offsetOf(slide) - scrolled);
      if (distance < best) {
        best = distance;
        index = i;
      }
    });

    dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === index);
    });
  }

  dots.forEach(function (dot, i) {
    dot.addEventListener('click', function () {
      const slide = slides[i];
      if (!slide) return;
      const offset = offsetOf(slide);
      track.scrollTo(
        isVertical() ? { top: offset, behavior: 'smooth' } : { left: offset, behavior: 'smooth' }
      );
    });
  });

  track.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
}

/** Product: the hero image track. */
function initProductPage(container) {
  const root = container.querySelector('[data-product]');
  if (!root || root.dataset.productReady === 'true') return;
  root.dataset.productReady = 'true';

  bindSliderDots(
    root.querySelector('[data-product-track]'),
    Array.from(root.querySelectorAll('[data-product-dots] [data-slide]'))
  );
}

/** All Exhibitions: one image track per exhibition. */
function initExhibitionsPage(container) {
  const root = container.querySelector('[data-exhibitions]');
  if (!root || root.dataset.exhibitionsReady === 'true') return;
  root.dataset.exhibitionsReady = 'true';

  root.querySelectorAll('[data-exhibition-media]').forEach(function (media) {
    bindSliderDots(
      media.querySelector('[data-exhibition-track]'),
      Array.from(media.querySelectorAll('[data-exhibition-dots] [data-slide]'))
    );
  });
}

/**
 * The CRM's register endpoint requires a password, but this form is a lead
 * capture — the visitor never chooses or sees one. Generate a throwaway value
 * so the request is accepted. Anyone who later wants to sign in goes through
 * password reset.
 *
 * One character from each set is included up front, so the value satisfies the
 * usual complexity rules whatever they turn out to be.
 */
function randomPassword(length) {
  const sets = [
    'ABCDEFGHJKLMNPQRSTUVWXYZ',
    'abcdefghijkmnopqrstuvwxyz',
    '23456789',
    '!@#$%^&*-_',
  ];
  const all = sets.join('');
  const values = new Uint32Array(length * 2);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(values);
  } else {
    for (let i = 0; i < values.length; i++) {
      values[i] = Math.floor(Math.random() * 0xffffffff);
    }
  }

  const chars = sets.map(function (set, i) {
    return set[values[i] % set.length];
  });
  for (let i = sets.length; i < length; i++) {
    chars.push(all[values[i] % all.length]);
  }

  // Shuffle so the guaranteed characters are not always in the first slots.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = values[length + i] % (i + 1);
    const swap = chars[i];
    chars[i] = chars[j];
    chars[j] = swap;
  }

  return chars.join('');
}

/**
 * Artist: the inline "Access Available Works" form.
 *
 * This is a lead capture, not an account signup — the available-works page is
 * not gated — so it collects name and email only and sends the visitor on.
 *
 */
function initArtistPage(container) {
  const form = container.querySelector('[data-artist-register]');
  if (!form || form.dataset.artistReady === 'true') return;
  form.dataset.artistReady = 'true';

  const endpoint = 'https://crm.gallerytwentytwenty.com/api/gallery/register';
  const message = form.querySelector('[data-artist-register-message]');
  const panel = container.querySelector('[data-artist-register-panel]');
  const collections = container.querySelector('[data-artist-collections]');

  // Swap the form for the artist's collections, grouped as on the Stockroom.
  function reveal() {
    if (panel) panel.hidden = true;
    if (collections) collections.hidden = false;
  }

  /*
   * Someone who has already registered should not be asked again, on this or
   * any other artist. That needs its own flag: gallery_token is the real auth
   * token, and writing it here would light up the nav's Logout, the Projects
   * link and the logged-in form prefills for someone who only left an email.
   * A login still counts, hence both.
   */
  const REGISTERED_KEY = 'gallery_registered';

  function hasRegistered() {
    try {
      return !!(localStorage.getItem(REGISTERED_KEY) || localStorage.getItem('gallery_token'));
    } catch (_) {
      return false;
    }
  }

  if (hasRegistered()) reveal();

  function setMessage(text, kind) {
    if (!message) return;
    message.textContent = text || '';
    message.dataset.kind = kind || '';
  }

  form.addEventListener('submit', async function (event) {
    event.preventDefault();

    const data = new FormData(form);
    const payload = {
      firstName: String(data.get('firstName') || '').trim(),
      lastName: String(data.get('lastName') || '').trim(),
      email: String(data.get('email') || '').trim(),
      password: randomPassword(24),
    };

    if (!payload.firstName || !payload.lastName || !payload.email) {
      setMessage('Please fill in your first name, last name and email.', 'error');
      return;
    }

    setMessage('', '');

    /*
     * The works open on submit whatever the CRM answers. This is lead capture
     * rather than a real gate, and someone who registered a year ago would
     * otherwise be turned away by a duplicate-email error. The request still
     * goes out so the lead is recorded; it just does not decide anything, so
     * it is not awaited and a failure only reaches the console.
     */
    try { localStorage.setItem(REGISTERED_KEY, '1'); } catch (_) {}
    form.reset();
    reveal();

    if (collections) {
      collections.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(function (error) {
      console.error('Artist register submit failed:', error);
    });
  });
}

/** One conveyor transition: strip has two panels, animate translateY(-100vh), then replace with new page only. */
function runTransition(newContentHTML, targetUrl, pendingHash) {
  const wrapper = getWrapper();
  const strip = getStrip();
  const currentContainer = getCurrentContainer();
  if (!wrapper || !strip || !currentContainer || !newContentHTML) {
    window.location.href = targetUrl;
    return;
  }

  // Update body class so header shows when leaving index
  updateBodyClassForUrl(targetUrl);

  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';

  currentContainer.style.height = '100vh';
  currentContainer.style.overflow = 'hidden';

  const newContainer = document.createElement('div');
  newContainer.setAttribute('data-barba', 'container');
  newContainer.style.minHeight = '100vh';
  newContainer.style.height = '100vh';
  newContainer.style.width = '100%';
  newContainer.style.position = 'relative';
  newContainer.style.overflow = 'hidden';
  newContainer.innerHTML = newContentHTML;
  // About page: show fully during conveyor (no separate fade) – same as Works
  const aboutInNew = newContainer.querySelector('.about-page');
  if (aboutInNew) aboutInNew.classList.add('about-page-loaded');

  strip.appendChild(newContainer);
  wrapper.classList.add('is-transitioning');
  strip.style.transition = `transform ${TRANSITION_DURATION_MS}ms ${EASING}`;
  strip.style.transform = 'translateY(0)';

  const onEnd = () => {
    strip.removeEventListener('transitionend', onEnd);
    strip.removeChild(currentContainer);
    strip.style.transition = '';
    strip.style.transform = '';
    wrapper.classList.remove('is-transitioning');
    newContainer.style.height = '';
    newContainer.style.overflow = '';
    window.scrollTo(0, 0);
    const finalUrl = targetUrl;
    if (window.history && window.history.pushState) {
      try {
        window.history.pushState(null, '', finalUrl);
      } catch (_) {}
    }
    updateBodyClassForUrl(finalUrl);
    initCurrentPage(newContainer, pendingHash);
  };

  strip.addEventListener('transitionend', onEnd);
  strip.offsetHeight;
  strip.style.transform = 'translateY(-100vh)';
}

/** Fade-in transition (used for work entry pages): replace content in place and fade in. */
function runFadeTransition(newContentHTML, targetUrl, pendingHash) {
  const currentContainer = getCurrentContainer();
  if (!currentContainer || !newContentHTML) {
    window.location.href = targetUrl;
    return;
  }

  updateBodyClassForUrl(targetUrl);
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';

  currentContainer.innerHTML = newContentHTML;
  const aboutInNew = currentContainer.querySelector('.about-page');
  if (aboutInNew) aboutInNew.classList.add('about-page-loaded');

  currentContainer.style.opacity = '0';
  currentContainer.style.transition = `opacity ${TRANSITION_DURATION_MS}ms ${EASING}`;
  currentContainer.offsetHeight;
  currentContainer.style.opacity = '1';

  let ended = false;
  const onEnd = () => {
    if (ended) return;
    ended = true;
    currentContainer.removeEventListener('transitionend', onEnd);
    currentContainer.style.transition = '';
    currentContainer.style.opacity = '';
    if (window.history && window.history.pushState) {
      try { window.history.pushState(null, '', targetUrl); } catch (_) {}
    }
    updateBodyClassForUrl(targetUrl);
    initCurrentPage(currentContainer, pendingHash);
    window.scrollTo(0, 0);
  };
  currentContainer.addEventListener('transitionend', onEnd);
  setTimeout(onEnd, TRANSITION_DURATION_MS + 50);
}

/** Conveyor transition with menu sliding up in sync (menu stays visible, both move together). */
function runTransitionWithMenuLeave(newContentHTML, targetUrl, pendingHash) {
  const wrapper = getWrapper();
  const strip = getStrip();
  const currentContainer = getCurrentContainer();
  const menuOverlay = document.getElementById('menu-overlay');
  if (!wrapper || !strip || !currentContainer || !newContentHTML) {
    window.location.href = targetUrl;
    return;
  }

  // Update body class immediately so header shows when leaving index (body.index-page hides header)
  updateBodyClassForUrl(targetUrl);

  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';

  currentContainer.style.height = '100vh';
  currentContainer.style.overflow = 'hidden';
  // Hide current page so we only see the menu sliding up and the new page sliding in
  currentContainer.style.visibility = 'hidden';

  const newContainer = document.createElement('div');
  newContainer.setAttribute('data-barba', 'container');
  newContainer.style.minHeight = '100vh';
  newContainer.style.height = '100vh';
  newContainer.style.width = '100%';
  newContainer.style.position = 'relative';
  newContainer.style.overflow = 'hidden';
  newContainer.innerHTML = newContentHTML;
  // About page: show fully during conveyor (no separate fade) – same as Works
  const aboutInNew = newContainer.querySelector('.about-page');
  if (aboutInNew) aboutInNew.classList.add('about-page-loaded');

  strip.appendChild(newContainer);
  wrapper.classList.add('is-transitioning');
  strip.style.transition = `transform ${TRANSITION_DURATION_MS}ms ${EASING}`;
  strip.style.transform = 'translateY(0)';

  // Animate menu up in sync with conveyor (same direction on all pages, including Works from-bottom menu)
  if (menuOverlay && (menuOverlay.style.display === 'flex' || window.getComputedStyle(menuOverlay).display === 'flex')) {
    menuOverlay.classList.remove('menu-overlay-slide-enter', 'menu-overlay-slide-enter-active');
    menuOverlay.classList.add('menu-overlay-slide-leave');
    menuOverlay.style.transition = `transform ${TRANSITION_DURATION_MS}ms ${EASING}`;
    menuOverlay.offsetHeight;
    menuOverlay.classList.add('menu-overlay-slide-leave-active');
    menuOverlay.style.transform = 'translateY(-100vh)';
  }

  const cleanupMenu = () => {
    if (menuOverlay) {
      window.menuOverlayIsOpen = false;
      if (window.setMenuOverlayState) window.setMenuOverlayState(false);
      menuOverlay.style.display = 'none';
      menuOverlay.style.visibility = '';
      menuOverlay.style.opacity = '';
      menuOverlay.style.zIndex = '';
      menuOverlay.style.transform = '';
      menuOverlay.style.transition = '';
      menuOverlay.classList.remove('menu-overlay-slide-leave', 'menu-overlay-slide-leave-active');
      document.body.classList.remove('menu-open');
    }
  };

  const onEnd = () => {
    strip.removeEventListener('transitionend', onEnd);
    strip.removeChild(currentContainer);
    strip.style.transition = '';
    strip.style.transform = '';
    wrapper.classList.remove('is-transitioning');
    newContainer.style.height = '';
    newContainer.style.overflow = '';
    cleanupMenu();
    window.scrollTo(0, 0);
    if (window.history && window.history.pushState) {
      try { window.history.pushState(null, '', targetUrl); } catch (_) {}
    }
    updateBodyClassForUrl(targetUrl);
    initCurrentPage(newContainer, pendingHash);
  };

  strip.addEventListener('transitionend', onEnd);
  strip.offsetHeight;
  strip.style.transform = 'translateY(-100vh)';
}

/** Conveyor transition with artists overlay sliding up in sync (artists list up, new page up from below). */
function runTransitionWithArtistsLeave(newContentHTML, targetUrl, pendingHash, cleanupArtistsOverlay) {
  const wrapper = getWrapper();
  const strip = getStrip();
  const currentContainer = getCurrentContainer();
  const artistsOverlayEl = document.getElementById('artists-overlay');
  if (!wrapper || !strip || !currentContainer || !newContentHTML) {
    window.location.href = targetUrl;
    return;
  }

  // Update body class immediately so header shows when leaving index
  updateBodyClassForUrl(targetUrl);

  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';

  currentContainer.style.height = '100vh';
  currentContainer.style.overflow = 'hidden';
  currentContainer.style.visibility = 'hidden';

  const newContainer = document.createElement('div');
  newContainer.setAttribute('data-barba', 'container');
  newContainer.style.minHeight = '100vh';
  newContainer.style.height = '100vh';
  newContainer.style.width = '100%';
  newContainer.style.position = 'relative';
  newContainer.style.overflow = 'hidden';
  newContainer.innerHTML = newContentHTML;
  const aboutInNew = newContainer.querySelector('.about-page');
  if (aboutInNew) aboutInNew.classList.add('about-page-loaded');

  strip.appendChild(newContainer);
  wrapper.classList.add('is-transitioning');
  strip.style.transition = `transform ${TRANSITION_DURATION_MS}ms ${EASING}`;
  strip.style.transform = 'translateY(0)';

  if (artistsOverlayEl && (artistsOverlayEl.style.display === 'flex' || window.getComputedStyle(artistsOverlayEl).display === 'flex')) {
    artistsOverlayEl.classList.remove('artists-overlay-slide-enter', 'artists-overlay-slide-enter-active');
    artistsOverlayEl.classList.add('artists-overlay-slide-leave');
    artistsOverlayEl.style.transition = `transform ${TRANSITION_DURATION_MS}ms ${EASING}`;
    artistsOverlayEl.offsetHeight;
    artistsOverlayEl.classList.add('artists-overlay-slide-leave-active');
    artistsOverlayEl.style.transform = 'translateY(-100vh)';
  }

  const onEnd = () => {
    strip.removeEventListener('transitionend', onEnd);
    strip.removeChild(currentContainer);
    strip.style.transition = '';
    strip.style.transform = '';
    wrapper.classList.remove('is-transitioning');
    newContainer.style.height = '';
    newContainer.style.overflow = '';
    if (typeof cleanupArtistsOverlay === 'function') cleanupArtistsOverlay();
    window.scrollTo(0, 0);
    if (window.history && window.history.pushState) {
      try { window.history.pushState(null, '', targetUrl); } catch (_) {}
    }
    updateBodyClassForUrl(targetUrl);
    initCurrentPage(newContainer, pendingHash);
  };

  strip.addEventListener('transitionend', onEnd);
  strip.offsetHeight;
  strip.style.transform = 'translateY(-100vh)';
}

/** Navigate to href: work entry uses fade; when menu is open, conveyor + menu slide up; else close overlays then conveyor. Conveyor only from home page; elsewhere just load. */
function navigate(href) {
  const parsed = (() => { try { return new URL(href, window.location.origin); } catch (_) { return null; } })();
  if (!parsed || parsed.origin !== window.location.origin) {
    window.location.href = href;
    return;
  }
  const targetUrl = parsed.pathname + (parsed.search || '') + (parsed.hash || '');
  const pendingHash = parsed.hash || null;
  const pathname = parsed.pathname || '';

  if (isWorkEntryUrl(pathname) || isExhibitionEntryUrl(pathname) || isAvailableWorksUrl(pathname)) {
    closeOverlaysBeforeTransition()
      .then(() => fetchPageContent(parsed.pathname + (parsed.search || '')))
      .then((html) => runFadeTransition(html, targetUrl, pendingHash))
      .catch(() => { window.location.href = href; });
    return;
  }

  // Conveyor transitions only from home page; on any other page just load
  if (!isIndexPath(window.location.pathname)) {
    window.location.href = href;
    return;
  }

  if (isMenuOpen()) {
    fetchPageContent(parsed.pathname + (parsed.search || ''))
      .then((html) => runTransitionWithMenuLeave(html, targetUrl, pendingHash))
      .catch(() => { window.location.href = href; });
  } else {
    closeOverlaysBeforeTransition().then(() => {
      return fetchPageContent(parsed.pathname + (parsed.search || ''));
    }).then((html) => {
      runTransition(html, targetUrl, pendingHash);
    }).catch(() => {
      window.location.href = href;
    });
  }
}

// Initialize work entry page (show content, thumbnail hover drives main image strip).
// Work entry page is scrollable; no body scroll lock.

// Exhibition entry page paging (left scroll drives right-hand copy pagination)
// Needs to be initialized when entering the page via transition (inline scripts don't run on injected HTML).
function initExhibitionEntryPage(container) {
  if (!container) return;
  const pageEl = container.querySelector('.exhibition-entry-page');
  if (!pageEl) return;

  // Mobile: no paging (continuous text), don't scroll-lock the window
  const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  if (isMobile) {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    return;
  }

  // Prevent double-binding if we re-enter the same container
  if (pageEl.dataset.pagingInitialized === 'true') return;
  pageEl.dataset.pagingInitialized = 'true';

  const leftCol =
    pageEl.querySelector('#exhibition-gallery-scroll') ||
    pageEl.querySelector('.exhibitions-left');
  const descriptionPages = pageEl.querySelectorAll('.exhibition-entry-description-page');
  const dots = pageEl.querySelectorAll('.exhibition-entry-dot');

  if (!leftCol || !descriptionPages.length || !dots.length) return;

  // This page uses internal scrolling; lock window scroll
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';

  const pageCount = Math.max(descriptionPages.length, dots.length);
  const contentWrapper =
    leftCol.querySelector('.exhibition-entry-gallery-wrapper') || leftCol;

  function getMaxContentScroll() {
    // Prefer measuring the actual gallery wrapper so the footer spacer region
    // (appended to the scroll container) never affects paging math.
    const contentHeight = contentWrapper === leftCol ? leftCol.scrollHeight : contentWrapper.offsetHeight;
    return Math.max(0, contentHeight - leftCol.clientHeight);
  }

  function updateActivePage(activeIndex) {
    const idx = Math.max(0, Math.min(activeIndex, pageCount - 1));
    descriptionPages.forEach((page, i) => {
      page.classList.toggle('active', i === idx);
    });
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === idx);
    });
  }

  function onScroll() {
    const scrollTop = leftCol.scrollTop;
    const scrollHeight = getMaxContentScroll();
    if (scrollHeight <= 0) {
      updateActivePage(0);
      return;
    }
    const clampedTop = Math.min(scrollTop, scrollHeight);
    const ratio = clampedTop / scrollHeight;
    const index = Math.min(Math.floor(ratio * pageCount), pageCount - 1);
    updateActivePage(index);
  }

  leftCol.addEventListener('scroll', onScroll, { passive: true });

  dots.forEach((dot, index) => {
    dot.addEventListener('click', function () {
      const scrollHeight = getMaxContentScroll();
      const targetScroll =
        scrollHeight <= 0
          ? 0
          : pageCount > 1
            ? (index / (pageCount - 1)) * scrollHeight
            : 0;
      leftCol.scrollTo({ top: targetScroll, behavior: 'smooth' });
      updateActivePage(index);
    });
  });

  // Initial state + a couple of delayed recalcs for image/layout shifts
  updateActivePage(0);
  setTimeout(onScroll, 50);
  setTimeout(onScroll, 250);
  setTimeout(onScroll, 700);
}

// Exhibition entry paging must also run on full page loads (e.g. user refreshes on exhibition entry URL).
(function initExhibitionEntryOnLoad() {
  function run() {
    const container = document.querySelector('[data-barba="container"]') || document;
    initExhibitionEntryPage(container);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  // Backup in case of late image/layout shifts.
  setTimeout(run, 200);
})();

// Works index: use event delegation so View All and gallery clicks work after both
// full page load and client-side navigation (the container is replaced on transition, so direct
// listeners would be attached to removed elements).
const WORKS_VIEW_ALL_LABEL_COLLAPSED = 'View All <span class="works-view-all-brackets">(</span> + <span class="works-view-all-brackets">)</span>';
const WORKS_VIEW_ALL_LABEL_EXPANDED = 'View All <span class="works-view-all-brackets">(</span> - <span class="works-view-all-brackets">)</span>';
const WORKS_COLLAPSE_DURATION = 900;

function setupWorksPageDelegation() {
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.works-page')) return;

    const viewAllBtn = e.target.closest('.works-view-all-toggle');
    if (viewAllBtn) {
      e.preventDefault();
      e.stopPropagation();
      const gallery = viewAllBtn.closest('.works-gallery');
      const wasExpanded = gallery.classList.contains('works-gallery--expanded');
      if (wasExpanded) {
        gallery.classList.add('works-gallery--collapsing');
        setTimeout(() => {
          gallery.classList.remove('works-gallery--expanded', 'works-gallery--collapsing');
          viewAllBtn.setAttribute('aria-expanded', 'false');
          viewAllBtn.innerHTML = WORKS_VIEW_ALL_LABEL_COLLAPSED;
        }, WORKS_COLLAPSE_DURATION);
      } else {
        gallery.classList.add('works-gallery--expanded');
        viewAllBtn.setAttribute('aria-expanded', 'true');
        viewAllBtn.innerHTML = WORKS_VIEW_ALL_LABEL_EXPANDED;
      }
      return;
    }

    // Gallery item clicks are handled by global link handler (navigate); only View All toggle here
  });
}

// Initialize everything: conveyor page transition + initial page inits
(function initApp() {
  // Post-login intent: used when a logged-out user clicks "+" to add a work to Projects.
  // We store the work + return URL in sessionStorage, then after login we resume the action.
  const POST_LOGIN_INTENT_KEY = 'gallery_post_login_intent_v1';
  function setPostLoginIntent(intent) {
    try {
      if (!intent || typeof intent !== 'object') return;
      sessionStorage.setItem(POST_LOGIN_INTENT_KEY, JSON.stringify(intent));
    } catch (_) {}
  }
  function getPostLoginIntent() {
    try {
      const raw = sessionStorage.getItem(POST_LOGIN_INTENT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  function clearPostLoginIntent() {
    try { sessionStorage.removeItem(POST_LOGIN_INTENT_KEY); } catch (_) {}
  }

  window.fulfillPostLoginIntent = function() {
    const hasToken = (() => {
      try {
        return typeof localStorage !== 'undefined' && !!localStorage.getItem('gallery_token');
      } catch (_) {
        return false;
      }
    })();
    if (!hasToken) return false;

    const intent = getPostLoginIntent();
    if (!intent) return false;

    const returnUrl = typeof intent.returnUrl === 'string' ? intent.returnUrl : '';
    const workToAdd = intent.workToAdd && typeof intent.workToAdd === 'object' ? intent.workToAdd : null;

    // If login happened on a different page, return to the original gallery URL first.
    if (returnUrl && returnUrl !== window.location.href) {
      // Keep intent for the destination page load.
      window.location.href = returnUrl;
      return true;
    }

    // Same page: consume intent and open Projects modal as if user was logged in at click time.
    clearPostLoginIntent();
    if (window.openProjectsModal) {
      if (workToAdd) window.openProjectsModal({ workToAdd });
      else window.openProjectsModal();
      return true;
    }
    return false;
  };

  /*
   * Click handler for the two in-page controls below. It no longer intercepts
   * navigation: pages change with a plain browser load rather than the
   * conveyor/fade transition, which the client asked to be instant everywhere.
   */
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a[href]');
    if (!link) return;
    if (e.target.closest('.works-gallery-plus-icon')) {
      e.preventDefault();
      e.stopPropagation();
      const plusEl = e.target.closest('.works-gallery-plus-icon');
      const isLoggedIn = typeof localStorage !== 'undefined' && !!localStorage.getItem('gallery_token');
      const d = plusEl && plusEl.dataset ? plusEl.dataset : {};
      const workToAdd = (d.workTitle != null || d.workImage != null || d.workUrl != null)
        ? { title: d.workTitle || '', image: d.workImage || '', link: d.workUrl || '' }
        : null;
      if (isLoggedIn && window.openProjectsModal) {
        window.openProjectsModal(workToAdd ? { workToAdd } : {});
      } else if (window.openLoginModal) {
        // Remember intent so after login we can resume "add to Projects" and return to this URL.
        if (workToAdd) {
          setPostLoginIntent({ type: 'projects_add_work', returnUrl: window.location.href, workToAdd });
        }
        window.openLoginModal();
      }
      return;
    }
    const enquireEl = e.target.closest('.works-gallery-item-enquire');
    if (enquireEl) {
      e.preventDefault();
      e.stopPropagation();
      if (window.openEnquireModal) {
        const d = enquireEl && enquireEl.dataset ? enquireEl.dataset : {};
        window.openEnquireModal({ workTitle: d.workTitle || '', artistName: d.artistName || '' });
      }
      return;
    }
    // Everything else navigates normally; nothing is intercepted.
  }, true);

  // Same-page hash links: close menu and scroll to target
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a[href*="#"]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || !href.includes('#')) return;
    try {
      const url = new URL(href, window.location.origin);
      if (url.pathname !== window.location.pathname || !url.hash) return;
      e.preventDefault();
      if (window.closeNavOverlay) window.closeNavOverlay();
      setTimeout(() => {
        const target = document.querySelector(url.hash);
        if (target) {
          const headerHeight = 80;
          const pos = target.getBoundingClientRect().top + (window.pageYOffset || 0) - headerHeight;
          window.scrollTo({ top: pos, behavior: 'smooth' });
        }
      }, 300);
    } catch (_) {}
  });

  // Back/forward: force full reload so content is shown (avoids blank page when restored from bfcache)
  window.navigate = navigate;
  setupWorksPageDelegation();

  // Initial page inits (same as after a transition)
  function initInitialPage() {
    const container = getCurrentContainer();
    if (container) initCurrentPage(container);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInitialPage);
  } else {
    initInitialPage();
  }
  setTimeout(initInitialPage, 150);
})();

// About page animation - ensure it loads on initial page load
(function() {
  function initAboutPage() {
    const aboutPage = document.querySelector('.about-page');
    if (aboutPage && !aboutPage.classList.contains('about-page-loaded')) {
      aboutPage.classList.add('about-page-loaded');
    }
  }
  
  // Run immediately if DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAboutPage);
  } else {
    initAboutPage();
  }
  
  // Also run after a short delay as backup
  setTimeout(initAboutPage, 100);
})();

// Contact modal - vanilla JS implementation
(function() {
  function initContactModal() {
    const modal = document.getElementById('contact-modal');
    const container = document.getElementById('contact-container');
    const closeBtn = document.getElementById('contact-close-btn');
    
    if (!modal || !container) {
      console.error('Modal elements not found!', { modal, container });
      return;
    }
  
  function openModal() {

    if (!modal || !container) {
      console.error('Modal or container not found!');
      return;
    }

    // Measured now rather than on load: opened from the menu overlay, the nav
    // is only laid out once that overlay is showing.
    syncContactPanelWidth();

    // Prefill from logged-in user (localStorage) so it works after client-side login or when form was rendered before login
    try {
      const hasToken = typeof localStorage !== 'undefined' && !!localStorage.getItem('gallery_token');
      if (hasToken) {
        let user = null;
        try {
          const raw = localStorage.getItem('gallery_user');
          user = raw ? JSON.parse(raw) : null;
        } catch (_) {
          user = null;
        }
        const getStr = (v) => (typeof v === 'string' ? v.trim() : '');
        const name = getStr(user && (user.name || user.fullName || user.full_name));
        const firstName = getStr(user && (user.firstName || user.first_name)) || (name ? name.split(' ')[0] : '');
        const lastName = getStr(user && (user.lastName || user.last_name)) || (name ? name.split(' ').slice(1).join(' ') : '');
        const email = getStr(user && user.email);
        const phone = getStr(user && (user.phone || user.phoneNumber || user.phone_number || user.mobile));
        const firstNameInput = document.getElementById('firstName');
        const lastNameInput = document.getElementById('lastName');
        const emailInput = document.getElementById('email');
        const phoneInput = document.getElementById('phone');
        if (firstNameInput && !firstNameInput.value && firstName) firstNameInput.value = firstName;
        if (lastNameInput && !lastNameInput.value && lastName) lastNameInput.value = lastName;
        if (emailInput && !emailInput.value && email) emailInput.value = email;
        if (phoneInput && !phoneInput.value && phone) phoneInput.value = phone;
      }
    } catch (_) {}

    modal.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Remove any leave classes
    container.classList.remove('slide-leave', 'slide-leave-active');
    
    // Set initial state (off-screen)
    container.classList.add('slide-enter');
    
    // Force a reflow to ensure the initial state is applied
    container.offsetHeight;
    
    // Trigger slide-in animation after a tiny delay
    setTimeout(() => {
      container.classList.remove('slide-enter');
      container.classList.add('slide-enter-active');
      // Focus firstName when modal is visible (autofocus doesn't work when form is hidden at load)
      const firstNameInput = document.getElementById('firstName');
      if (firstNameInput) firstNameInput.focus();
    }, 10);
  }
  
  function closeModal() {
    // Trigger slide-out animation
    container.classList.remove('slide-enter', 'slide-enter-active');
    container.classList.add('slide-leave');
    requestAnimationFrame(() => {
      container.classList.add('slide-leave-active');
    });
    
    // Hide modal after animation
    setTimeout(() => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      container.classList.remove('slide-leave', 'slide-leave-active');
    }, 2000); // Match transition duration
  }
  
  // Event listeners
  window.addEventListener('contact-modal-open', openModal);
  window.addEventListener('contact-modal-close', closeModal);
  
  if (closeBtn) {
    closeBtn.addEventListener('click', closeModal);
  }
  
  // Close on backdrop click
  modal.addEventListener('click', function(e) {
    if (e.target === modal) {
      closeModal();
    }
  });
  
  // Close on ESC key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && modal.style.display === 'block') {
      closeModal();
    }
  });
  
    // Expose functions globally
    window.openContactModal = openModal;
    window.closeContactModal = closeModal;
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactModal);
  } else {
    initContactModal();
  }
})();

// Enquire modal - vanilla JS, separate from contact for future functionality
(function() {
  function initEnquireModal() {
    const modal = document.getElementById('enquire-modal');
    const container = document.getElementById('enquire-container');
    const closeBtn = document.getElementById('enquire-close-btn');
    const firstNameInput = document.getElementById('enquire-firstName');
    const lastNameInput = document.getElementById('enquire-lastName');
    const emailInput = document.getElementById('enquire-email');
    const phoneInput = document.getElementById('enquire-phone');
    const workOfInterestInput = document.getElementById('enquire-workOfInterest');
    const moreInformationTextarea = document.getElementById('enquire-moreInformation');
    
    if (!modal || !container) {
      return;
    }
  
    function openModal(prefill) {
      if (!modal || !container) return;

      try {
        // Prefill logged-in user details (do not override any existing user input)
        const hasToken = (() => {
          try {
            return typeof localStorage !== 'undefined' && !!localStorage.getItem('gallery_token');
          } catch (_) {
            return false;
          }
        })();

        if (hasToken) {
          let user = null;
          try {
            const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('gallery_user') : null;
            user = raw ? JSON.parse(raw) : null;
          } catch (_) {
            user = null;
          }

          const getStr = (v) => (typeof v === 'string' ? v.trim() : '');
          const name = getStr(user && (user.name || user.fullName || user.full_name));
          const firstName =
            getStr(user && (user.firstName || user.first_name)) || (name ? name.split(' ')[0] : '');
          const lastName =
            getStr(user && (user.lastName || user.last_name)) ||
            (name ? name.split(' ').slice(1).join(' ') : '');
          const email = getStr(user && user.email);
          const phone = getStr(user && (user.phone || user.phoneNumber || user.phone_number || user.mobile));

          if (firstNameInput && !firstNameInput.value && firstName) firstNameInput.value = firstName;
          if (lastNameInput && !lastNameInput.value && lastName) lastNameInput.value = lastName;
          if (emailInput && !emailInput.value && email) emailInput.value = email;
          if (phoneInput && !phoneInput.value && phone) phoneInput.value = phone;
        }

        if (prefill && typeof prefill === 'object') {
          const workTitle = typeof prefill.workTitle === 'string' ? prefill.workTitle.trim() : '';
          const artistName = typeof prefill.artistName === 'string' ? prefill.artistName.trim() : '';
          const workOfInterest =
            typeof prefill.workOfInterest === 'string' ? prefill.workOfInterest.trim() : workTitle;
          const moreInformation =
            typeof prefill.moreInformation === 'string'
              ? prefill.moreInformation
              : (workTitle
                  ? `I would like to request more information about the ${workTitle}${artistName ? ` by ${artistName}.` : ''}`
                  : '');

          if (workOfInterestInput && workOfInterest) {
            workOfInterestInput.value = workOfInterest;
          }
          if (moreInformationTextarea && moreInformation) {
            moreInformationTextarea.value = moreInformation;
          }
        }
      } catch (_) {
        // If prefill fails for any reason, still open the modal.
      }

      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
      container.classList.remove('slide-leave', 'slide-leave-active');
      container.classList.add('slide-enter');
      container.offsetHeight;
      setTimeout(() => {
        container.classList.remove('slide-enter');
        container.classList.add('slide-enter-active');
      }, 10);
    }
  
    function closeModal() {
      container.classList.remove('slide-enter', 'slide-enter-active');
      container.classList.add('slide-leave');
      requestAnimationFrame(() => container.classList.add('slide-leave-active'));
      setTimeout(() => {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        container.classList.remove('slide-leave', 'slide-leave-active');
      }, 2000);
    }
  
    window.addEventListener('enquire-modal-open', openModal);
    window.addEventListener('enquire-modal-close', closeModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function(e) {
      if (e.target === modal) closeModal();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.style.display === 'block') closeModal();
    });
  
    window.openEnquireModal = openModal;
    window.closeEnquireModal = closeModal;
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEnquireModal);
  } else {
    initEnquireModal();
  }
})();

// Catalogue modal - same slide behaviour as enquire
(function() {
  function initCatalogueModal() {
    const modal = document.getElementById('catalogue-modal');
    const container = document.getElementById('catalogue-container');
    const closeBtn = document.getElementById('catalogue-close-btn');

    if (!modal || !container) {
      return;
    }

    function openModal() {
      if (!modal || !container) return;

      // Prefill logged-in user details (do not override any existing user input)
      try {
        const hasToken = (() => {
          try {
            return typeof localStorage !== 'undefined' && !!localStorage.getItem('gallery_token');
          } catch (_) {
            return false;
          }
        })();

        if (hasToken) {
          let user = null;
          try {
            const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('gallery_user') : null;
            user = raw ? JSON.parse(raw) : null;
          } catch (_) {
            user = null;
          }

          const getStr = (v) => (typeof v === 'string' ? v.trim() : '');
          const name = getStr(user && (user.name || user.fullName || user.full_name));
          const firstName =
            getStr(user && (user.firstName || user.first_name)) || (name ? name.split(' ')[0] : '');
          const lastName =
            getStr(user && (user.lastName || user.last_name)) ||
            (name ? name.split(' ').slice(1).join(' ') : '');
          const email = getStr(user && user.email);
          const phone = getStr(user && (user.phone || user.phoneNumber || user.phone_number || user.mobile));
          const company = getStr(user && (user.company || user.organisation || user.organization));

          const firstNameInput = document.getElementById('catalogue-firstName');
          const lastNameInput = document.getElementById('catalogue-lastName');
          const emailInput = document.getElementById('catalogue-email');
          const phoneInput = document.getElementById('catalogue-phone');
          const companyInput = document.getElementById('catalogue-company');

          if (firstNameInput && !firstNameInput.value && firstName) firstNameInput.value = firstName;
          if (lastNameInput && !lastNameInput.value && lastName) lastNameInput.value = lastName;
          if (emailInput && !emailInput.value && email) emailInput.value = email;
          if (phoneInput && !phoneInput.value && phone) phoneInput.value = phone;
          if (companyInput && !companyInput.value && company) companyInput.value = company;
        }
      } catch (_) {
        // If prefill fails for any reason, still open the modal.
      }

      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
      container.classList.remove('slide-leave', 'slide-leave-active');
      container.classList.add('slide-enter');
      container.offsetHeight;
      setTimeout(() => {
        container.classList.remove('slide-enter');
        container.classList.add('slide-enter-active');
      }, 10);
    }

    function closeModal() {
      container.classList.remove('slide-enter', 'slide-enter-active');
      container.classList.add('slide-leave');
      requestAnimationFrame(() => container.classList.add('slide-leave-active'));
      setTimeout(() => {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        container.classList.remove('slide-leave', 'slide-leave-active');
      }, 2000);
    }

    window.addEventListener('catalogue-modal-open', openModal);
    window.addEventListener('catalogue-modal-close', closeModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function(e) {
      if (e.target === modal) closeModal();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.style.display === 'block') closeModal();
    });

    window.openCatalogueModal = openModal;
    window.closeCatalogueModal = closeModal;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCatalogueModal);
  } else {
    initCatalogueModal();
  }
})();

// Projects modal - same slide behaviour as catalogue
(function() {
  function initProjectsModal() {
    const modal = document.getElementById('projects-modal');
    const container = document.getElementById('projects-container');
    const closeBtn = document.getElementById('projects-close-btn');
    let closeTimeoutId = null;
    let openAnimTimeoutId = null;
    let enableBackdropCloseTimeoutId = null;
    let backdropCloseEnabled = true;

    if (!modal || !container) {
      return;
    }

    function openModal() {
      if (!modal || !container) return;
      // If we reopen before the previous close animation finishes,
      // cancel the pending "hide modal" timeout so it doesn't immediately disappear.
      if (closeTimeoutId) {
        clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
      }
      if (openAnimTimeoutId) {
        clearTimeout(openAnimTimeoutId);
        openAnimTimeoutId = null;
      }
      if (enableBackdropCloseTimeoutId) {
        clearTimeout(enableBackdropCloseTimeoutId);
        enableBackdropCloseTimeoutId = null;
      }
      backdropCloseEnabled = false;
      enableBackdropCloseTimeoutId = setTimeout(() => {
        enableBackdropCloseTimeoutId = null;
        backdropCloseEnabled = true;
      }, 450);

      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
      container.classList.remove('slide-leave', 'slide-leave-active');
      container.classList.add('slide-enter');
      container.offsetHeight;
      openAnimTimeoutId = setTimeout(() => {
        openAnimTimeoutId = null;
        container.classList.remove('slide-enter');
        container.classList.add('slide-enter-active');
      }, 10);
    }

    function closeModal() {
      // Prevent stacked close timers (double-close can happen via quick clicks/backdrop).
      if (closeTimeoutId) {
        clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
      }
      if (openAnimTimeoutId) {
        clearTimeout(openAnimTimeoutId);
        openAnimTimeoutId = null;
      }
      container.classList.remove('slide-enter', 'slide-enter-active');
      container.classList.add('slide-leave');
      requestAnimationFrame(() => container.classList.add('slide-leave-active'));
      closeTimeoutId = setTimeout(() => {
        closeTimeoutId = null;
        modal.style.display = 'none';
        document.body.style.overflow = '';
        container.classList.remove('slide-leave', 'slide-leave-active');
      }, 1000);
    }

    window.addEventListener('projects-modal-close', closeModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function(e) {
      if (e.target === modal && backdropCloseEnabled) closeModal();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.style.display === 'block') closeModal();
    });

    // Footer copyright: when logged in (localStorage), click opens projects modal instead of going to /
    document.addEventListener('click', function(e) {
      const link = e.target.closest('.site-footer-copyright-link');
      if (!link) return;
      const isLoggedIn = typeof localStorage !== 'undefined' && !!localStorage.getItem('gallery_token');
      if (isLoggedIn) {
        e.preventDefault();
        if (window.openProjectsModal) window.openProjectsModal();
        else openModal();
      }
    });

    window.openProjectsModal = function(options) {
      window.dispatchEvent(new CustomEvent('projects-modal-open', { detail: options || {} }));
      openModal();
    };
    window.closeProjectsModal = closeModal;

    // If we just returned from login and have a stored "+" intent, fulfill it now.
    // (At this point openProjectsModal exists, so we can safely open the modal.)
    setTimeout(() => {
      if (window.fulfillPostLoginIntent) window.fulfillPostLoginIntent();
    }, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProjectsModal);
  } else {
    initProjectsModal();
  }
})();

// Login modal - vanilla JS implementation
(function() {
  function initLoginModal() {
    const modal = document.getElementById('login-modal');
    const container = document.getElementById('login-container');
    const closeBtn = document.getElementById('login-close-btn');
    
    if (!modal || !container) {
      return;
    }

    function focusEmail() {
      const email = document.getElementById('loginEmail');
      if (!email) return;
      try {
        // preventScroll is not supported everywhere; keep it best-effort.
        email.focus({ preventScroll: true });
      } catch (_) {
        email.focus();
      }
    }

    // After the slide-in finishes, ensure Email has focus.
    container.addEventListener('transitionend', (e) => {
      if (e.propertyName !== 'transform') return;
      if (modal.style.display !== 'block') return;
      if (!container.classList.contains('slide-enter-active')) return;
      focusEmail();
    });
  
  function openModal() {
    if (!modal || !container) {
      return;
    }
    
    modal.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Remove any leave classes
    container.classList.remove('slide-leave', 'slide-leave-active');
    
    // Set initial state (off-screen)
    container.classList.add('slide-enter');
    
    // Force a reflow to ensure the initial state is applied
    container.offsetHeight;
    
    // Trigger slide-in animation after a tiny delay
    setTimeout(() => {
      container.classList.remove('slide-enter');
      container.classList.add('slide-enter-active');
      focusEmail();
    }, 10);
  }
  
  function closeModal() {
    // Trigger slide-out animation
    container.classList.remove('slide-enter', 'slide-enter-active');
    container.classList.add('slide-leave');
    requestAnimationFrame(() => {
      container.classList.add('slide-leave-active');
    });
    
    // Hide modal after animation
    setTimeout(() => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      container.classList.remove('slide-leave', 'slide-leave-active');
    }, 1000); // Match transition duration
  }
  
  // Event listeners
  window.addEventListener('login-modal-open', openModal);
  window.addEventListener('login-modal-close', closeModal);
  
  if (closeBtn) {
    closeBtn.addEventListener('click', closeModal);
  }
  
  // Close on backdrop click
  modal.addEventListener('click', function(e) {
    if (e.target === modal) {
      closeModal();
    }
  });
  
  // Close on ESC key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && modal.style.display === 'block') {
      closeModal();
    }
  });
  
    // Expose functions globally
    window.openLoginModal = openModal;
    window.closeLoginModal = closeModal;
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoginModal);
  } else {
    initLoginModal();
  }
})();

// Register modal - vanilla JS implementation (same pattern as login modal)
(function() {
  function initRegisterModal() {
    const modal = document.getElementById('register-modal');
    const container = document.getElementById('register-container');
    const closeBtn = document.getElementById('register-close-btn');

    if (!modal || !container) {
      return;
    }

    function openModal() {
      if (!modal || !container) {
        return;
      }

      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';

      container.classList.remove('slide-leave', 'slide-leave-active');
      container.classList.add('slide-enter');
      container.offsetHeight;

      setTimeout(() => {
        container.classList.remove('slide-enter');
        container.classList.add('slide-enter-active');
      }, 10);
    }

    function closeModal() {
      container.classList.remove('slide-enter', 'slide-enter-active');
      container.classList.add('slide-leave');
      requestAnimationFrame(() => {
        container.classList.add('slide-leave-active');
      });

      setTimeout(() => {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        container.classList.remove('slide-leave', 'slide-leave-active');
      }, 1000);
    }

    window.addEventListener('register-modal-open', openModal);
    window.addEventListener('register-modal-close', closeModal);

    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    modal.addEventListener('click', function(e) {
      if (e.target === modal) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.style.display === 'block') {
        closeModal();
      }
    });

    window.openRegisterModal = openModal;
    window.closeRegisterModal = closeModal;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRegisterModal);
  } else {
    initRegisterModal();
  }
})();

// Auth nav: Login / Logout button in site nav (updates from localStorage, logout calls API)
(function() {
  const LOGOUT_ENDPOINT = 'https://crm.gallerytwentytwenty.com/api/gallery/logout';

  function isLoggedIn() {
    try {
      return typeof localStorage !== 'undefined' && !!localStorage.getItem('gallery_token');
    } catch (_) {
      return false;
    }
  }

  function doLogout() {
    const token = localStorage.getItem('gallery_token');
    if (!token) {
      try { localStorage.removeItem('gallery_user'); } catch (_) {}
      updateAuthNav();
      return;
    }
    fetch(LOGOUT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
      },
    }).catch(function() {}).finally(function() {
      try {
        localStorage.removeItem('gallery_token');
        localStorage.removeItem('gallery_user');
      } catch (_) {}
      updateAuthNav();
    });
  }

  const PROJECTS_API = 'https://crm.gallerytwentytwenty.com/api/gallery/projects';

  function updateProjectsCount() {
    const countEls = document.querySelectorAll('.menu-overlay-projects-count');
    if (!countEls.length) return;
    try {
      const token = localStorage.getItem('gallery_token');
      if (!token) return;
      fetch(PROJECTS_API, {
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
      })
        .then(function(res) { return res.json(); })
        .then(function(data) {
          const projects = Array.isArray(data) ? data
            : (data && data.projects) ? data.projects
            : (data && data.data) ? (Array.isArray(data.data) ? data.data : (data.data.projects || [])) : [];
          const n = projects.length;
          countEls.forEach(function(el) { el.textContent = '( ' + n + ' )'; });
        })
        .catch(function() {
          countEls.forEach(function(el) { el.textContent = '( — )'; });
        });
    } catch (_) {
      countEls.forEach(function(el) { el.textContent = '( — )'; });
    }
  }

  function updateAuthNav() {
    const buttons = document.querySelectorAll('.site-nav-auth-btn');
    const menuLinks = document.querySelectorAll('.menu-overlay-auth-link');
    const projectsLinks = document.querySelectorAll('.menu-overlay-projects-link');

    if (isLoggedIn()) {
      buttons.forEach(function(btn) {
        btn.textContent = 'logout';
        btn.onclick = doLogout;
      });
      projectsLinks.forEach(function(link) {
        link.style.display = '';
        link.onclick = function(e) {
          e.preventDefault();
          if (window.closeNavOverlay) window.closeNavOverlay();
          if (window.openProjectsModal) window.openProjectsModal();
          return false;
        };
      });
      updateProjectsCount();
      menuLinks.forEach(function(link) {
        var textSpan = link.querySelector('.menu-overlay-text');
        if (textSpan) textSpan.textContent = 'Logout';
        link.onclick = function(e) {
          e.preventDefault();
          doLogout();
          return false;
        };
      });
    } else {
      buttons.forEach(function(btn) {
        btn.textContent = 'login';
        btn.onclick = function() {
          if (window.openLoginModal) window.openLoginModal();
        };
      });
      projectsLinks.forEach(function(link) {
        link.style.display = 'none';
        link.onclick = null;
      });
      menuLinks.forEach(function(link) {
        var textSpan = link.querySelector('.menu-overlay-text');
        if (textSpan) textSpan.textContent = 'Login';
        link.onclick = function(e) {
          e.preventDefault();
          if (window.openLoginModal) window.openLoginModal();
          return false;
        };
      });
    }
  }

  window.updateAuthNav = updateAuthNav;
  window.updateProjectsCount = updateProjectsCount;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAuthNav);
  } else {
    updateAuthNav();
  }
})();

// Menu overlay - vanilla JS implementation
(function() {
  let overlay = null;
  let isOpen = false;
  
  // Expose isOpen state for transitions
  window.getMenuOverlayState = function() {
    return { overlay, isOpen };
  };
  
  window.setMenuOverlayState = function(state) {
    isOpen = state;
  };
  
  // Store menu state in a way that persists across navigation
  window.menuOverlayIsOpen = false;
  
  function openNavOverlay() {
    // Get overlay if not already cached
    if (!overlay) {
      overlay = document.getElementById('menu-overlay');
    }
    
    if (!overlay) {
      console.warn('Menu overlay not found');
      return;
    }
    
    // Close artists overlay if open - wait for it to close before opening menu
    const artistsOverlay = document.getElementById('artists-overlay');
    const isArtistsOpen = (window.isArtistsOverlayOpen && window.isArtistsOverlayOpen()) || 
                          (artistsOverlay && (artistsOverlay.style.display === 'flex' || window.getComputedStyle(artistsOverlay).display === 'flex'));
    
    if (window.closeArtistsOverlay && isArtistsOpen) {
      window.closeArtistsOverlay();
      // Wait for artists overlay to close (850ms animation) before opening menu
      setTimeout(() => {
        // Continue with opening menu overlay - force open (don't toggle)
        continueOpenNavOverlay(true);
      }, 850);
      return;
    }
    
    continueOpenNavOverlay(false);
  }
  
  function continueOpenNavOverlay(forceOpen) {
    // Get overlay if not already cached
    if (!overlay) {
      overlay = document.getElementById('menu-overlay');
    }
    
    if (!overlay) {
      console.warn('Menu overlay not found');
      return;
    }
    
    // Menu from-bottom only on index page; Works and About (and others) get menu from top; artists overlay is the only from-bottom elsewhere
    const showFromBottom = document.body.classList.contains('index-page');
    if (showFromBottom) {
      if (!overlay.classList.contains('menu-overlay-from-bottom')) {
        overlay.classList.add('menu-overlay-from-bottom');
      }
    } else {
      overlay.classList.remove('menu-overlay-from-bottom');
    }
    
    // Only toggle if not forcing open (coming from artists overlay)
    if (!forceOpen && isOpen) {
      closeNavOverlay();
      return;
    }
    
    // If already open and forcing, just return (menu is already open)
    if (forceOpen && isOpen) {
      return;
    }
    
    // Check if this is the from-bottom variant
    const isFromBottom = overlay.classList.contains('menu-overlay-from-bottom');
    
    isOpen = true;
    window.menuOverlayIsOpen = true; // Set global flag
    // Use flex for all variants to enable vertical centering
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    
    document.body.classList.add('menu-open');
    document.body.style.overflow = 'hidden';
    
    // Hide standalone bottom-bar on index page (it's now inside the overlay)
    if (isFromBottom) {
      const bottomBar = document.getElementById('bottom-bar') || document.querySelector('.bottom-bar#bottom-bar') || document.querySelector('.bottom-bar');
      if (bottomBar && !bottomBar.closest('.menu-overlay')) {
        bottomBar.style.display = 'none';
      }
    }
    
    // Get content to push (different for index vs other pages)
    const currentContainer = document.querySelector('[data-barba="container"]');
    const mainContent = currentContainer ? currentContainer.querySelector('main') : null;
    const galleryContainer = document.querySelector('.gallery-container'); // For index.twig
    
    // Remove any leave classes
    overlay.classList.remove('menu-overlay-slide-leave', 'menu-overlay-slide-leave-active');
    
    if (!isFromBottom) {
      // Non-home: menu just appears (no slide)
      overlay.style.transform = 'translateY(0)';
      if (mainContent) {
        mainContent.style.transition = 'none';
        mainContent.style.transform = 'translateY(100vh)';
      }
      return;
    }
    
    // Home page: slide-up-from-bottom animation
    overlay.classList.add('menu-overlay-slide-enter');
    overlay.style.transform = 'translateY(100%)';
    if (galleryContainer) {
      galleryContainer.style.transform = 'translateY(0)';
      galleryContainer.style.transition = 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    }
    
    overlay.offsetHeight;
    if (galleryContainer) galleryContainer.offsetHeight;
    
    setTimeout(() => {
      overlay.classList.remove('menu-overlay-slide-enter');
      overlay.classList.add('menu-overlay-slide-enter-active');
      overlay.style.transform = 'translateY(0)';
      if (galleryContainer) {
        galleryContainer.style.transform = 'translateY(-100vh)';
      }
    }, 10);
  }
  
  function closeNavOverlay() {
    if (!overlay) {
      overlay = document.getElementById('menu-overlay');
    }
    
    if (!overlay || !isOpen) return;
    
    isOpen = false;
    window.menuOverlayIsOpen = false; // Clear global flag
    
    // Check if this is the from-bottom variant
    const isFromBottom = overlay.classList.contains('menu-overlay-from-bottom');
    
    // Get content to slide back (different for index vs other pages)
    const currentContainer = document.querySelector('[data-barba="container"]');
    const mainContent = currentContainer ? currentContainer.querySelector('main') : null;
    const galleryContainer = document.querySelector('.gallery-container'); // For index.twig
    
    if (!isFromBottom) {
      // Non-home: menu just disappears (no slide)
      overlay.style.display = 'none';
      overlay.style.transform = '';
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
      overlay.classList.remove('menu-overlay-slide-leave', 'menu-overlay-slide-leave-active');
      if (mainContent) {
        mainContent.style.transition = '';
        mainContent.style.transform = '';
      }
      return;
    }
    
    // Home page: slide animation
    overlay.classList.remove('menu-overlay-slide-enter', 'menu-overlay-slide-enter-active');
    overlay.classList.add('menu-overlay-slide-leave');
    overlay.offsetHeight;
    setTimeout(() => {
      overlay.classList.add('menu-overlay-slide-leave-active');
      overlay.style.transform = 'translateY(100%)';
      if (galleryContainer) {
        galleryContainer.style.transform = 'translateY(0)';
        galleryContainer.style.transition = 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      }
    }, 10);
    
    // Hide overlay after animation - use slightly longer timeout to ensure animation completes
    setTimeout(() => {
      overlay.style.display = 'none';
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
      overlay.classList.remove('menu-overlay-slide-leave', 'menu-overlay-slide-leave-active');
      overlay.style.transform = ''; // Reset transform
      
      // Reset container and main content transforms
      if (currentContainer) {
        currentContainer.style.transition = '';
        currentContainer.style.transform = '';
      }
      const mainContent = currentContainer ? currentContainer.querySelector('main') : null;
      if (mainContent) {
        mainContent.style.transition = '';
        mainContent.style.transform = '';
      }
      // Reset gallery-container for index page
      const galleryContainer = document.querySelector('.gallery-container');
      if (galleryContainer) {
        galleryContainer.style.transition = '';
        galleryContainer.style.transform = '';
      }
      
      // Show standalone bottom-bar again on index page - delay slightly to prevent flash
      if (isFromBottom) {
        setTimeout(() => {
          const bottomBar = document.getElementById('bottom-bar');
          if (bottomBar) {
            bottomBar.style.display = 'flex';
          }
        }, 50); // Small delay to prevent flash
      }
    }, 850); // Slightly longer than transition duration to ensure it completes
  }
  
  // Expose functions globally immediately
  window.openNavOverlay = openNavOverlay;
  window.closeNavOverlay = closeNavOverlay;
  
  function initNavOverlay() {
    overlay = document.getElementById('menu-overlay');
    if (!overlay) return;
    
    // Menu link clicks are handled by the global link handler (navigate() runs conveyor/fade).
    const menuLinks = overlay.querySelectorAll('.menu-overlay-item');
    menuLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        // Don't close for artists link (it opens artists overlay)
        if (link.getAttribute('onclick') && link.getAttribute('onclick').includes('openArtistsOverlay')) {
          return;
        }
        // Don't manually close – the transition will slide the menu up and the new page in
      });
    });
    
    // Close on ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) {
        closeNavOverlay();
      }
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNavOverlay);
  } else {
    initNavOverlay();
  }
  
})();

// Artists overlay - vanilla JS implementation
(function() {
  let artistsOverlay = null;
  let isArtistsOpen = false;
  
  // Helper function to get image elements dynamically
  function getImageElements() {
    return {
      leftImage: document.querySelector('.artist-image-left-img'),
      rightImage: document.querySelector('.artist-image-right-img'),
      leftImageContainer: document.querySelector('.artists-overlay-image-left'),
      rightImageContainer: document.querySelector('.artists-overlay-image-right')
    };
  }
  
  // Expose state getter immediately
  window.isArtistsOverlayOpen = function() {
    return isArtistsOpen;
  };
  
  function openArtistsOverlay() {
    if (!artistsOverlay) {
      artistsOverlay = document.getElementById('artists-overlay');
    }
    
    if (!artistsOverlay) {
      console.warn('Artists overlay not found');
      return;
    }
    
    if (isArtistsOpen) {
      closeArtistsOverlay();
      return;
    }
    
    const menuOverlay = document.getElementById('menu-overlay');
    const menuIsOpen = menuOverlay && (window.menuOverlayIsOpen === true || document.body.classList.contains('menu-open') ||
      (window.getComputedStyle(menuOverlay).display === 'flex'));
    
    if (menuIsOpen) {
      const onHome = typeof isIndexPath === 'function' && isIndexPath(window.location.pathname);
      isArtistsOpen = true;
      document.body.classList.add('artists-open');
      document.body.style.overflow = 'hidden';
      initArtistsHover();
      artistsOverlay.classList.remove('artists-overlay-slide-leave', 'artists-overlay-slide-leave-active');
      artistsOverlay.style.display = 'flex';
      artistsOverlay.style.flexDirection = 'column';

      const cleanupMenu = () => {
        window.menuOverlayIsOpen = false;
        if (window.setMenuOverlayState) window.setMenuOverlayState(false);
        menuOverlay.style.display = 'none';
        menuOverlay.style.visibility = '';
        menuOverlay.style.opacity = '';
        menuOverlay.style.zIndex = '';
        menuOverlay.style.transform = '';
        menuOverlay.style.transition = '';
        menuOverlay.classList.remove('menu-overlay-slide-leave', 'menu-overlay-slide-leave-active');
        document.body.classList.remove('menu-open');
      };

      if (onHome) {
        // Home only: menu slides up, artists slides up from bottom in sync
        artistsOverlay.style.transform = 'translateY(100%)';
        artistsOverlay.style.transition = `transform ${TRANSITION_DURATION_MS}ms ${EASING}`;
        artistsOverlay.offsetHeight;
        menuOverlay.classList.remove('menu-overlay-slide-enter', 'menu-overlay-slide-enter-active');
        menuOverlay.classList.add('menu-overlay-slide-leave');
        menuOverlay.style.transition = `transform ${TRANSITION_DURATION_MS}ms ${EASING}`;
        menuOverlay.offsetHeight;
        menuOverlay.classList.add('menu-overlay-slide-leave-active');
        menuOverlay.style.transform = 'translateY(-100vh)';
        artistsOverlay.style.transform = 'translateY(0)';
        setTimeout(cleanupMenu, TRANSITION_DURATION_MS);
      } else {
        artistsOverlay.style.transform = 'translateY(0)';
        cleanupMenu();
      }
      return;
    }

    // Menu not open: open artists (slide up from bottom on home only)
    if (window.closeNavOverlay) window.closeNavOverlay();

    isArtistsOpen = true;
    document.body.classList.add('artists-open');
    artistsOverlay.style.display = 'flex';
    artistsOverlay.style.flexDirection = 'column';
    document.body.style.overflow = 'hidden';
    initArtistsHover();
    artistsOverlay.classList.remove('artists-overlay-slide-leave', 'artists-overlay-slide-leave-active');

    const onHome = typeof isIndexPath === 'function' && isIndexPath(window.location.pathname);
    if (onHome) {
      artistsOverlay.classList.add('artists-overlay-slide-enter');
      artistsOverlay.style.transform = 'translateY(100%)';
      artistsOverlay.offsetHeight;
      setTimeout(() => {
        artistsOverlay.classList.remove('artists-overlay-slide-enter');
        artistsOverlay.classList.add('artists-overlay-slide-enter-active');
        artistsOverlay.style.transform = 'translateY(0)';
      }, 10);
    } else {
      artistsOverlay.style.transform = 'translateY(0)';
    }
  }
  
  function closeArtistsOverlay() {
    if (!artistsOverlay || !isArtistsOpen) return;

    isArtistsOpen = false;
    stopArtistCycle();
    const { leftImage, rightImage, leftImageContainer, rightImageContainer } = getImageElements();
    if (leftImageContainer) leftImageContainer.classList.remove('show');
    if (rightImageContainer) rightImageContainer.classList.remove('show');
    if (leftImage) leftImage.style.display = 'none';
    if (rightImage) rightImage.style.display = 'none';

    const onHome = typeof isIndexPath === 'function' && isIndexPath(window.location.pathname);
    if (!onHome) {
      artistsOverlay.style.display = 'none';
      document.body.style.overflow = '';
      document.body.classList.remove('artists-open');
      artistsOverlay.style.transform = '';
      artistsOverlay.classList.remove('artists-overlay-slide-enter', 'artists-overlay-slide-enter-active', 'artists-overlay-slide-leave', 'artists-overlay-slide-leave-active');
      return;
    }

    artistsOverlay.classList.remove('artists-overlay-slide-enter', 'artists-overlay-slide-enter-active');
    artistsOverlay.classList.add('artists-overlay-slide-leave');
    artistsOverlay.style.transform = 'translateY(0)';
    artistsOverlay.offsetHeight;
    setTimeout(() => {
      artistsOverlay.classList.add('artists-overlay-slide-leave-active');
      artistsOverlay.style.transform = 'translateY(100%)';
    }, 10);
    setTimeout(() => {
      artistsOverlay.style.display = 'none';
      document.body.style.overflow = '';
      document.body.classList.remove('artists-open');
      artistsOverlay.classList.remove('artists-overlay-slide-leave', 'artists-overlay-slide-leave-active');
      artistsOverlay.style.transform = '';
    }, 850);
  }
  
  /*
   * Mobile has no hover, so the artist list takes itself for a walk: each name
   * gets a turn in black with its image in the thumbnail. "All Artists" sits
   * out — it has no image of its own, so it is matched on data-left-image
   * rather than by position.
   */
  const ARTIST_CYCLE_MS = 2000;
  let artistCycleTimer = null;

  function setActiveArtist(items, index) {
    items.forEach((item, i) => item.classList.toggle('is-active', i === index));

    const { leftImage, leftImageContainer } = getImageElements();
    if (!leftImage || !leftImageContainer) return;

    const src = items[index] && items[index].dataset.leftImage;
    if (src) {
      leftImage.src = src;
      leftImage.style.display = 'block';
      leftImageContainer.classList.add('show');
    } else {
      leftImageContainer.classList.remove('show');
      leftImage.style.display = 'none';
    }
  }

  function startArtistCycle() {
    stopArtistCycle();
    if (!isMobileViewport()) return;

    const items = Array.from(document.querySelectorAll('.artists-list-item[data-left-image]'));
    if (!items.length) return;

    let index = 0;
    setActiveArtist(items, index);
    artistCycleTimer = setInterval(function () {
      index = (index + 1) % items.length;
      setActiveArtist(items, index);
    }, ARTIST_CYCLE_MS);
  }

  function stopArtistCycle() {
    if (artistCycleTimer) {
      clearInterval(artistCycleTimer);
      artistCycleTimer = null;
    }
    document.querySelectorAll('.artists-list-item.is-active').forEach(function (item) {
      item.classList.remove('is-active');
    });
  }

  // Handle artist item hover (desktop only; the cycle stands in on mobile)
  const initializedItems = new WeakSet();
  function initArtistsHover() {
    if (isMobileViewport()) {
      startArtistCycle();
      return;
    }

    const artistItems = document.querySelectorAll('.artists-list-item');
    artistItems.forEach(item => {
      if (initializedItems.has(item)) return;

      item.addEventListener('mouseenter', function () {
        const leftImg = item.dataset.leftImage;
        const rightImg = item.dataset.rightImage;
        const { leftImage, rightImage, leftImageContainer, rightImageContainer } = getImageElements();
        if (leftImg && leftImageContainer && leftImage) {
          leftImage.src = leftImg;
          leftImage.style.display = 'block';
          leftImageContainer.classList.add('show');
        }
        if (rightImg && rightImageContainer && rightImage) {
          rightImage.src = rightImg;
          rightImage.style.display = 'block';
          rightImageContainer.classList.add('show');
        }
      });

      item.addEventListener('mouseleave', function () {
        const { leftImage, rightImage, leftImageContainer, rightImageContainer } = getImageElements();
        if (leftImageContainer) leftImageContainer.classList.remove('show');
        if (rightImageContainer) rightImageContainer.classList.remove('show');
        if (leftImage) leftImage.style.display = 'none';
        if (rightImage) rightImage.style.display = 'none';
      });

      initializedItems.add(item);
    });
  }
  
  // Expose functions globally
  window.openArtistsOverlay = openArtistsOverlay;
  window.closeArtistsOverlay = closeArtistsOverlay;
  
  // Initialize when DOM is ready
  function initArtistsOverlay() {
    artistsOverlay = document.getElementById('artists-overlay');
    if (artistsOverlay) {
      initArtistsHover();
    }
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initArtistsOverlay);
  } else {
    initArtistsOverlay();
  }
})();

// Footer scroll reveal
(function() {
  let scrollHandler = null;
  let scrollElement = null;
  let ticking = false;
  let activeSmoother = null;
  const DEFAULT_FOOTER_HEIGHT = 400;
  /** Scroll distance over which the footer is revealed (larger = smoother, smaller = snappier). */
  const FOOTER_REVEAL_BUFFER_PX = 200;

  function createPxSmoother(applyPx) {
    let current = 0;
    let target = 0;
    let rafId = null;
    const EPS = 0.5;
    const ALPHA = 0.22;

    function frame() {
      rafId = null;
      current += (target - current) * ALPHA;
      if (Math.abs(target - current) <= EPS) current = target;
      applyPx(current);
      if (current !== target) rafId = window.requestAnimationFrame(frame);
    }

    function setTarget(px) {
      const next = Math.max(0, Number.isFinite(px) ? px : 0);
      target = next;
      if (rafId == null) rafId = window.requestAnimationFrame(frame);
    }

    function stop() {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function reset() {
      stop();
      current = 0;
      target = 0;
      applyPx(0);
    }

    return { setTarget, stop, reset };
  }

  function checkFooterVisibility() {
    const footer = document.querySelector('.site-footer');
    if (!footer) return;

    const windowHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Calculate how far from the bottom we are
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    
    // Get footer height for gradual reveal calculation
    const footerHeight = footer.offsetHeight || 200;
    // Use a larger reveal distance (4x footer height) for more gradual reveal
    const revealDistance = footerHeight * 8;
    
    // Start revealing when within reveal distance of the bottom
    if (distanceFromBottom > revealDistance) {
      // Before reaching reveal point, keep footer fully hidden
      footer.style.transform = 'translateY(100%)';
    } else {
      // Calculate reveal progress (0 to 1) - closer to bottom = more revealed
      const revealProgress = Math.max(0, 1 - (distanceFromBottom / revealDistance));
      
      // Gradually reveal footer: translateY goes from 100% (hidden) to 0% (visible)
      const translateY = 100 - (revealProgress * 100);
      footer.style.transform = `translateY(${translateY}%)`;
    }
  }

  function initFooterReveal(container) {
    const root = container && container.nodeType === 1 ? container : document;
    const footer = root.querySelector('.site-footer');
    if (!footer) return;
    const main = root.querySelector('main');

    // Footer is fixed and does not move/fade. The main content scroll reveals it.
    // Ensure no inline styles/classes from previous implementations linger.
    footer.classList.remove('footer-revealed', 'site-footer--bottom-reveal', 'site-footer--revealed');
    footer.style.transform = '';
    footer.style.opacity = '';
    footer.style.pointerEvents = '';
    // Stop any in-flight smoothing from a prior init (e.g. after a page transition).
    if (activeSmoother) {
      activeSmoother.reset();
      activeSmoother = null;
    }
    // Remove any prior reveal handler (safe when re-initing after transition)
    if (scrollHandler) {
      if (scrollElement) scrollElement.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('resize', scrollHandler);
      scrollHandler = null;
      scrollElement = null;
    }

    // Special-case: exhibition entry uses internal scrolling (window scroll locked),
    // so we reveal the footer by moving the page content up (footer stays fixed behind).
    const exhibitionPage = root.querySelector('.exhibition-entry-page[data-no-scroll]');
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    if (exhibitionPage && isMobile) {
      // Mobile exhibition entry uses normal document scrolling and should not
      // shift the entire main content up (can hide the page header on short pages).
      exhibitionPage.style.transform = '';
      if (main) {
        main.style.transform = '';
        main.style.paddingBottom = '';
      }
      return;
    }
    if (exhibitionPage && !isMobile) {
      const leftCol =
        exhibitionPage.querySelector('#exhibition-gallery-scroll') ||
        exhibitionPage.querySelector('.exhibitions-left');

      if (!leftCol) return;

      const smoother = createPxSmoother((px) => {
        if (px <= 0) exhibitionPage.style.transform = '';
        else exhibitionPage.style.transform = `translate3d(0, -${px}px, 0)`;
      });
      activeSmoother = smoother;

      // Add a scroll buffer at the end of the left column so the user can
      // keep scrolling *after* the last image, and we can progressively reveal
      // the footer only once they've reached the real end of content.
      let spacer = leftCol.querySelector('.js-footer-reveal-spacer');
      if (!spacer) {
        spacer = document.createElement('div');
        spacer.className = 'js-footer-reveal-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        spacer.style.flex = '0 0 auto';
        spacer.style.width = '1px';
        spacer.style.height = '0px';
        leftCol.appendChild(spacer);
      }

      const updateFromLeftScroll = function () {
        // Reveal only during the "buffer" region at the very end.
        // We move the exhibition page content up by the footer height,
        // exposing the fixed footer behind it.
        const footerHeight = footer.offsetHeight || DEFAULT_FOOTER_HEIGHT;
        const revealDistance = footerHeight;

        // Keep spacer in sync with revealDistance so:
        // - At end-of-content, distanceFromBottom === revealDistance => footer hidden
        // - As user scrolls through buffer, distanceFromBottom goes to 0 => footer fully revealed
        spacer.style.height = `${revealDistance}px`;

        const distanceFromBottom = leftCol.scrollHeight - (leftCol.scrollTop + leftCol.clientHeight);

        if (distanceFromBottom >= revealDistance) {
          smoother.setTarget(0);
          return;
        }

        const progress = Math.max(0, Math.min(1, 1 - distanceFromBottom / revealDistance));
        const translateYpx = footerHeight * progress;
        smoother.setTarget(translateYpx);
      };

      // Use a separate handler so we can remove it if we re-init
      scrollHandler = function () {
        if (!ticking) {
          window.requestAnimationFrame(function () {
            updateFromLeftScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      scrollElement = leftCol;
      leftCol.addEventListener('scroll', scrollHandler, { passive: true });
      window.addEventListener('resize', scrollHandler, { passive: true });

      // Initial + delayed updates for layout/image shifts
      updateFromLeftScroll();
      setTimeout(updateFromLeftScroll, 150);
      window.addEventListener('load', updateFromLeftScroll, { once: true, passive: true });
      return;
    }

    // Default pages: reveal by moving MAIN up near document bottom (footer never moves).
    if (!main) return;
    main.style.willChange = 'transform';
    const smoother = createPxSmoother((px) => {
      if (px <= 0) main.style.transform = '';
      else main.style.transform = `translate3d(0, -${px}px, 0)`;
    });
    activeSmoother = smoother;

    const updateFromWindowScroll = function () {
      const footerHeight = footer.offsetHeight || DEFAULT_FOOTER_HEIGHT;
      const revealDistance = FOOTER_REVEAL_BUFFER_PX;
      // Only add enough scroll room for the reveal buffer, not full footer height (avoids huge white band).
      main.style.paddingBottom = `${revealDistance}px`;

      const windowHeight = window.innerHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

      if (distanceFromBottom >= revealDistance) {
        smoother.setTarget(0);
        return;
      }

      const progress = Math.max(0, Math.min(1, 1 - distanceFromBottom / revealDistance));
      const translateYpx = footerHeight * progress;
      smoother.setTarget(translateYpx);
    };

    scrollHandler = function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          updateFromWindowScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    scrollElement = window;
    window.addEventListener('scroll', scrollHandler, { passive: true });
    window.addEventListener('resize', scrollHandler, { passive: true });
    updateFromWindowScroll();
    setTimeout(updateFromWindowScroll, 150);
    window.addEventListener('load', updateFromWindowScroll, { once: true, passive: true });
  }

  // Expose for transition/init to call after page content is swapped
  window.initFooterReveal = initFooterReveal;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFooterReveal);
  } else {
    initFooterReveal();
  }
})();
